import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

import { buildApiUrl } from '@core/api/api-url';
import { APP_CONFIG } from '@core/app-config';

type QueryParamValue = string | number | boolean;

export interface ApiRequestOptions {
  readonly params?: HttpParams | Record<string, QueryParamValue | readonly QueryParamValue[]>;
  readonly headers?: HttpHeaders | Record<string, string | string[]>;
  readonly withCredentials?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ApiClient {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(APP_CONFIG).apiBaseUrl;

  get<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.get<T>(this.url(path), options);
  }

  post<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.post<T>(this.url(path), body, options);
  }

  put<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.put<T>(this.url(path), body, options);
  }

  /** A PUT whose answer carries headers the caller needs, such as the new `ETag`. */
  putForResponse<T>(
    path: string,
    body: unknown,
    options?: ApiRequestOptions,
  ): Observable<HttpResponse<T>> {
    return this.http.put<T>(this.url(path), body, { ...options, observe: 'response' });
  }

  patch<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.patch<T>(this.url(path), body, options);
  }

  delete<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.delete<T>(this.url(path), options);
  }

  private url(path: string): string {
    return buildApiUrl(this.apiBaseUrl, path);
  }
}

/** The `version` from a detail read. Sent back on writes so a stale one fails with 412. */
export type ResourceVersion = number | string;

export function ifMatch(version: ResourceVersion): ApiRequestOptions {
  return { headers: { 'If-Match': `"${version}"` } };
}

/**
 * The version in an `ETag` header: `"912400"` → `912400`. Null when the header is missing,
 * which is also what the browser reports if the server doesn't expose it cross-origin.
 */
export function versionFromEtag(etag: string | null): ResourceVersion | null {
  const value = etag?.replace(/^W\//, '').replace(/"/g, '').trim();
  if (!value) {
    return null;
  }
  return /^\d+$/.test(value) ? Number(value) : value;
}
